import React, { Component } from 'react';
import moment from 'moment-timezone';
import CustomModal from '../../../util_components/custom_modal';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format_year, time_format } from '../../../../constants';
import {
  step_label,
  category_label,
  origin_label,
  origin_tone,
  category_tone,
  tone_style,
  fmt_amount,
  reason_label,
  student_label
} from './helpers';

// PROD-9152: every event behind one list row. A single failure can be seen by more than one writer
// — our servers, the Stripe webhook and the browser beacon — so each gets its own colour.
function Badge({ tone, children }) {
  return <span style={tone_style(tone)}>{children}</span>
}

function Field({ label, children }) {
  return <div>
    <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-label)' }}>
      {label}
    </div>
    <div style={{ color: 'var(--text-primary)' }}>{children}</div>
  </div>
}

// textAlign is explicit: the global table CSS centres td, which left the body out of step with
// the header.
const cell = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-default)',
  verticalAlign: 'top',
  textAlign: 'left'
};
const head = {
  ...cell,
  fontSize: 11,
  textTransform: 'uppercase',
  color: 'var(--text-label)'
};

// Fixed widths stop Time and Step wrapping and give Reason the slack instead.
const WIDTHS = ['90px', '150px', '110px', 'auto', '120px', '170px'];

class EventsModal extends Component {
  state = { loading: true, events: [] };

  componentDidMount() {
    const { group } = this.props;
    const url = get_api_url(
      `/v2/cms_user/payment_failures/${encodeURIComponent(group.group_id)}/events`,
      false,
      true
    );

    get_api(url, true)
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        this.setState({ loading: false, events: data.events || [] });
      })
      .catch(() => this.setState({ loading: false, events: [] }));
  }

  // The ids differ per event; showing whichever one this event carries keeps the column narrow.
  stripe_ref = (event) =>
    event.stripe_payment_intent_id || event.stripe_setup_intent_id || event.stripe_charge_id || '';

  render_row = (event) => (
    <tr key={event.id}>
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        {moment(event.occurred_at).format(time_format)}
      </td>
      <td style={cell}>{step_label(event.step)}</td>
      <td style={cell}>
        <Badge tone={origin_tone(event.origin)}>{origin_label(event.origin)}</Badge>
      </td>
      <td style={cell}>
        <div style={{ color: 'var(--text-primary)' }}>{reason_label(event)}</div>
        {event.error_message ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{event.error_message}</div>
        ) : null}
      </td>
      <td style={cell}>
        <Badge tone={category_tone(event.error_category)}>
          {category_label(event.error_category)}
        </Badge>
      </td>
      <td style={{ ...cell, fontSize: 12, color: 'var(--text-muted)' }}>
        {event.error_type ? <div>{event.error_type}</div> : null}
        {event.network_decline_code ? <div>network {event.network_decline_code}</div> : null}
        {event.advice_code ? <div>{event.advice_code}</div> : null}
        {this.stripe_ref(event) ? <div>{this.stripe_ref(event)}</div> : null}
      </td>
    </tr>
  );

  render() {
    const { group, close } = this.props;
    const { loading, events } = this.state;

    return (
      <CustomModal show close={close}>
        <div style={{ minWidth: 720, maxWidth: 980, color: 'var(--text-primary)' }}>
          <h4 style={{ marginBottom: 12 }}>Payment attempt</h4>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 24,
              padding: 12,
              marginBottom: 16,
              background: 'var(--surface-sunken)',
              borderRadius: 6
            }}
          >
            <Field label="Student">{student_label(group) || 'Not resolved'}</Field>
            <Field label="Amount">{fmt_amount(group.amount, group.currency)}</Field>
            <Field label="Country">{group.country || '—'}</Field>
            <Field label="First attempt">
              {moment(group.first_attempt).format(day_format_year)}{' '}
              {moment(group.first_attempt).format(time_format)}
            </Field>
            <Field label="Reference">
              <span style={{ fontSize: 12 }}>{group.group_id}</span>
            </Field>
          </div>

          {loading ? (
            <div>Loading…</div>
          ) : events.length === 0 ? (
            <div>No events found for this attempt.</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                {events.length} event{events.length === 1 ? '' : 's'}, oldest first
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  {WIDTHS.map((w, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th style={head}>Time</th>
                    <th style={head}>Step</th>
                    <th style={head}>Seen by</th>
                    <th style={head}>Reason</th>
                    <th style={head}>Category</th>
                    <th style={head}>Details</th>
                  </tr>
                </thead>
                <tbody>{events.map(this.render_row)}</tbody>
              </table>
            </>
          )}
        </div>
      </CustomModal>
    );
  }
}

export default EventsModal;
